import React, { useEffect, useMemo, useRef, useState } from "react";
import { getVideoPlaybackCandidates } from "../../../utils/videoPlaybackUrls";

/**
 * Reliable video playback with URL fallbacks. Thumbnail clicks use VideoThumbnailButton; modal uses autoplay + optional controls (muted so autoplay is allowed).
 */
export default function PlaybackVideo({
  rawUrl,
  className,
  label,
  fallbackClassName,
  showControls = false,
  autoPlay = true,
  loop = true,
}) {
  const candidates = useMemo(
    () => getVideoPlaybackCandidates(rawUrl ?? ""),
    [rawUrl]
  );
  const [index, setIndex] = useState(0);
  const [exhausted, setExhausted] = useState(false);
  const ref = useRef(null);
  const n = candidates.length;

  useEffect(() => {
    setIndex(0);
    setExhausted(false);
  }, [rawUrl, n]);

  const src = n > 0 && index >= 0 && index < n ? candidates[index] : null;

  useEffect(() => {
    const el = ref.current;
    if (!el || !src || !autoPlay) return;
    const tryPlay = () => {
      el.play?.().catch(() => {});
    };
    el.addEventListener("loadeddata", tryPlay);
    el.addEventListener("canplay", tryPlay);
    tryPlay();
    return () => {
      el.removeEventListener("loadeddata", tryPlay);
      el.removeEventListener("canplay", tryPlay);
    };
  }, [src, autoPlay]);

  const handleError = () => {
    setIndex((prev) => {
      const next = prev + 1;
      if (next < n) return next;
      setExhausted(true);
      return prev;
    });
  };

  if (!String(rawUrl ?? "").trim()) {
    return null;
  }

  if (!n || exhausted || !src) {
    return (
      <div
        className={fallbackClassName ?? "video-playback-unavailable"}
        title={String(rawUrl).slice(0, 500)}
      >
        Can&apos;t load video
      </div>
    );
  }

  return (
    <video
      ref={ref}
      key={src}
      className={className}
      src={src}
      muted
      loop={loop}
      playsInline
      autoPlay={autoPlay}
      preload={autoPlay ? "auto" : "metadata"}
      controls={showControls}
      onError={handleError}
      aria-label={label}
    />
  );
}
